import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * SearchDropdown
 *
 * Displays search results with type badges and keyboard navigation.
 * Results are mixed together with type indicators.
 */
function SearchDropdown({ results, selectedIndex, onSelect, onClose }) {
  const navigate = useNavigate();

  if (!results || results.length === 0) {
    return (
      <div className="absolute z-50 w-full mt-1 rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
        <div className="px-4 py-3 text-sm text-gray-400">No results found</div>
      </div>
    );
  }

  const handleResultClick = (result, index) => {
    onSelect(result);
    navigate(result.route);
    onClose();
  };

  const getTypeBadgeColor = (type) => {
    switch (type) {
      case 'project':
        return 'bg-blue-600 text-white';
      case 'review':
        return 'bg-purple-600 text-white';
      case 'task':
        return 'bg-green-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'project':
        return 'Project';
      case 'review':
        return 'Review';
      case 'task':
        return 'Task';
      default:
        return type;
    }
  };

  return (
    <div className="absolute z-50 w-full mt-1 rounded-lg border border-gray-700 bg-gray-900 shadow-xl max-h-96 overflow-y-auto">
      {results.map((result, index) => (
        <div
          key={`${result.type}-${result.id}`}
          onClick={() => handleResultClick(result, index)}
          className={`flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors ${
            index === selectedIndex
              ? 'bg-blue-600/20 border-l-2 border-blue-500'
              : 'hover:bg-gray-800 border-l-2 border-transparent'
          }`}
        >
          <span
            className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${getTypeBadgeColor(
              result.type
            )}`}
          >
            {getTypeLabel(result.type)}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-100 truncate">{result.title}</p>
            {result.subtitle && (
              <p className="text-[10px] text-gray-500 truncate">{result.subtitle}</p>
            )}
          </div>
          {index === selectedIndex && (
            <span className="text-gray-500 text-[10px]">↵</span>
          )}
        </div>
      ))}
    </div>
  );
}

export default SearchDropdown;
